import { zodResolver } from '@hookform/resolvers/zod'
import { Body, Headline } from '@shipt/design-system-typography'
import { FeedbackBanner } from '@shipt/design-system-banner'
import { routes } from '@shared/constants/routes'
import {
  ungatedWebFormSchema,
  type UngatedWebFormData,
} from '@/features/entrypoint/components/Marketing/utils/validations'
import { getErrorMessage } from '@/features/shared/utils/dataFetching/utils'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { type PlaceholderData } from '@/features/cms/components/CMS/types'
import { Button } from '@shipt/design-system-buttons'
import { Column, Row } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { TextInput } from '@shipt/design-system-inputs'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Checkbox } from '@shipt/design-system-checkbox'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { useMutation } from '@tanstack/react-query'
import { apiPost } from '@/features/shared/utils/dataFetching'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { AnalyticsEvent } from '@shipt/analytics-member-web'
import { track } from '@/features/shared/analytics/trackingQueue'
import { theme } from '@/features/shared/constants/shiptTheme'

type UngatedWebFormPayload = Omit<UngatedWebFormData, 'email_consent'>

const submitUngatedWebForm = (formData: UngatedWebFormPayload) =>
  apiPost({
    config: {
      url: '/comms-data-etl/v1/users/ungated',
      data: JSON.stringify(formData),
      headers: { 'Content-Type': 'application/json' },
    },
    fetcherName: 'submitUngatedWebForm',
  })

export const UngatedWebForm = ({
  id,
  content_type_id,
}: Pick<PlaceholderData, 'content_type_id' | 'id'>) => {
  const {
    mutate: submitForm,
    isPending,
    isSuccess,
    error,
  } = useMutation({
    mutationFn: submitUngatedWebForm,
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<UngatedWebFormData>({
    resolver: zodResolver(ungatedWebFormSchema),
    mode: 'onChange',
  })

  const getFormErrorMessage = (e: unknown) => {
    const errorMsg = getErrorMessage(e, 'An error occurred while submitting the form.')
    return errorMsg.toLowerCase().includes('forbidden')
      ? 'It looks like we already have your information on file.'
      : errorMsg
  }

  const onSubmit = (data: UngatedWebFormData) => {
    trackElementClicked({
      content: 'submit',
      type: 'button',
      display_sublocation: 'ungated_signup_form',
    })
    submitForm(
      {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        zip_code: data.zip_code,
      },
      {
        onSuccess: () => {
          track({
            eventName: AnalyticsEvent.FormSubmitted,
            properties: {
              location: 'ungated_signup_form',
              opt_in_email: data.email_consent,
              opt_in_sms: false,
              zip_code: data.zip_code,
            },
          })
          reset()
        },
        onError: (err) => {
          trackElementViewed({
            content: `error_${getFormErrorMessage(err)}`,
          })
        },
      },
    )
  }

  return (
    <FormWrapper contentMaxWidth {...getCMSWrapperProps({ content_type_id, id })}>
      {isSuccess ? (
        <ConfirmationContainer
          align="center"
          justify="center"
          spacing="xl"
          padding={{ base: ['xxl'] }}
        >
          <Headline as="h2" size="xxl" align="center">
            Thanks for signing up!
          </Headline>
          <Body variant="secondary" align="center">
            We’ll send a welcome message pronto to confirm we’ve got all the right info.
          </Body>
          <Body variant="secondary" align="center">
            Meanwhile – need anything from the store?
          </Body>
          <ButtonLink
            href={routes.GLOBAL_HOMEPAGE.url}
            onClick={() =>
              trackElementClicked({
                content: 'start_shopping',
                next_location: 'global_homepage',
                type: 'button',
                display_sublocation: 'ungated_signup_form',
              })
            }
            fullWidth
          >
            Start Shopping
          </ButtonLink>
        </ConfirmationContainer>
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          {!!error && (
            <ErrorBanner
              hasIcon
              variant="negative"
              title={getFormErrorMessage(error)}
              description="Please try again or contact support if the issue persists."
            />
          )}
          <FormGrid>
            <Column>
              <TextInput
                id="first_name"
                label="First Name"
                placeholder="First Name"
                {...register('first_name')}
                errorMessage={errors.first_name?.message}
              />
            </Column>

            <Column>
              <TextInput
                id="last_name"
                label="Last Name"
                placeholder="Last Name"
                {...register('last_name')}
                errorMessage={errors.last_name?.message}
              />
            </Column>

            <Column>
              <TextInput
                id="email"
                label="Email"
                type="email"
                placeholder="Email"
                {...register('email')}
                errorMessage={errors.email?.message}
              />
            </Column>

            <Column>
              <TextInput
                id="zip"
                label="Zip Code (Optional)"
                type="text"
                placeholder="Zip Code"
                maxLength={5}
                inputMode="numeric"
                autoComplete="postal-code"
                {...register('zip_code')}
                errorMessage={errors.zip_code?.message}
              />
            </Column>
          </FormGrid>
          <Column spacing="xl" margin={{ base: ['lg', 0] }}>
            <Row>
              <Checkbox
                {...register('email_consent')}
                id="email_consent"
                size="sm"
                label="By opting in I agree to receiving marketing emails from Shipt."
                onChange={(e) => {
                  register('email_consent').onChange(e)
                  trackElementClicked({
                    content: e.target.checked ? 'email_opt_in_selected' : 'email_opt_in_deselected',
                    type: 'checkbox',
                  })
                }}
                errorMessage={errors.email_consent?.message}
              />
            </Row>

            <Button fullWidth type="submit" disabled={isPending || !isValid}>
              {isPending ? 'Submitting...' : 'Submit'}
            </Button>
          </Column>
        </Form>
      )}
    </FormWrapper>
  )
}

const Form = styled.form`
  width: 100%;
  padding: 2rem 0;
  margin: 0 auto;
`

const FormWrapper = styled(GridLayoutContainer)`
  background-color: ${theme.color.background.base.brand.secondary};
`

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: ${theme.spacing.lg};

  @media ${screenSizes.max_mobile} {
    grid-template-columns: 1fr;
  }
`

const ErrorBanner = styled(FeedbackBanner)`
  margin-bottom: 1rem;
`

const ConfirmationContainer = styled(Column)`
  margin: 0 auto;

  @media ${screenSizes.smDesktop} {
    max-width: 29.68rem;
  }
`
